
import { useEffect, useState } from "react";
import axios from "axios";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  ShieldCheck,
  Clock,
  Building2,
  Loader2,
  AlertCircle,
} from "lucide-react";

// ============================================================
// INFRA V5 SUPREME ADMIN API
// ============================================================

const API = (
  process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com"
).replace(/\/+$/, "");

function normalizeActive(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    (value &&
      typeof value === "object" &&
      (value[0] === 1 || value.data?.[0] === 1))
  );
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ============================================================
// MAIN MODAL
// ============================================================

export default function CompanyDetailsModal({
  open,
  tenantId,
  username,
  companyName,
  onClose,
}) {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ============================================================
  // FETCH COMPANY DETAILS
  // ============================================================

  useEffect(() => {
    if (!open || !tenantId || !username) {
      setRow(null);
      setErr("");
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setErr("");
      setRow(null);

      try {
        const url =
          `${API}/api/mysql/tenant/${Number(tenantId)}` +
          `/admins/${encodeURIComponent(String(username).trim())}`;

        const { data } = await axios.get(url, {
          withCredentials: true,
          timeout: 20000,
          signal: controller.signal,
        });

        if (
          data?.success === false ||
          data?.ok === false
        ) {
          throw new Error(
            data?.message ||
            data?.error ||
            "Failed to fetch company details."
          );
        }

        const record = data?.row ?? data?.data?.row ?? null;

        if (record) {
          setRow({
            ...record,
            is_active: normalizeActive(record.is_active),
          });
        } else {
          setRow(null);
        }
      } catch (error) {
        if (axios.isCancel(error)) return;

        console.error("Company details error:", error);

        setRow(null);

        setErr(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load company details."
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => controller.abort();
  }, [open, tenantId, username]);

  if (!open) return null;

  const titleId = "company-details-title";

  const titleCompany =
    row?.company_name || companyName || "Company Details";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className="w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* ================================================= */}
        {/* HEADER                                            */}
        {/* ================================================= */}

        <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-emerald-50 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
              <Building2 size={22} />
            </div>

            <div className="min-w-0">
              <h3
                id={titleId}
                className="truncate text-base font-bold text-slate-900"
              >
                Company Details
              </h3>

              <p className="mt-0.5 truncate text-xs text-slate-500">
                {titleCompany}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-slate-500 transition hover:bg-emerald-100 hover:text-slate-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* ================================================= */}
        {/* BODY                                              */}
        {/* ================================================= */}

        <div className="max-h-[75vh] overflow-auto px-5 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-14 text-slate-600">
              <Loader2
                size={30}
                className="animate-spin text-emerald-600"
              />

              <p className="text-sm font-medium">
                Loading company details...
              </p>
            </div>
          ) : err ? (
            <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
              <AlertCircle
                size={32}
                className="text-rose-500"
              />

              <p className="text-sm font-semibold text-rose-600">
                Unable to Load Company Details
              </p>

              <p className="max-w-lg text-sm text-slate-500">
                {err}
              </p>
            </div>
          ) : !row ? (
            <div className="flex flex-col items-center justify-center gap-3 py-14 text-slate-500">
              <Building2 size={34} className="text-slate-300" />

              <p className="text-sm font-medium">
                No record found for this administrator.
              </p>
            </div>
          ) : (
            <AdminCard record={row} tenantId={tenantId} />
          )}
        </div>

        {/* FOOTER */}

        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ADMIN DETAILS CARD
// ============================================================

function AdminCard({ record, tenantId }) {
  const statusClass = record.is_active
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
    : "bg-rose-50 text-rose-700 ring-rose-200";

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      {/* ADMIN HEADER */}

      <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600 text-white">
            <User size={20} />
          </div>

          <div>
            <p className="text-base font-bold text-slate-900">
              {record.username || "—"}
            </p>

            <p className="mt-0.5 text-xs text-slate-500">
              {record.company_name || "—"}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusClass}`}
          >
            <ShieldCheck size={13} />
            {record.is_active ? "Active" : "Inactive"}
          </span>

          {record.updated_at_utc && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs text-slate-600 ring-1 ring-slate-200">
              <Clock size={13} />
              {formatDate(record.updated_at_utc)}
            </span>
          )}
        </div>
      </div>

      {/* COMPANY INFORMATION */}

      <div className="p-5">
        <div className="mb-5">
          <h4 className="text-sm font-bold text-slate-900">
            Company Information
          </h4>

          <p className="mt-1 text-xs text-slate-500">
            Registered tenant administrator and contact information.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Tenant ID"
            icon={Building2}
            value={tenantId}
          />

          <Field
            label="Company Name"
            icon={Building2}
            value={record.company_name}
          />

          <Field
            label="Administrator Username"
            icon={User}
            value={record.username}
          />

          <Field
            label="Contact Name"
            icon={User}
            value={record.contact_name}
          />

          <Field
            label="Email Address"
            icon={Mail}
            value={record.email}
          />

          <Field
            label="Phone Number"
            icon={Phone}
            value={record.phone}
          />

          <Field
            className="sm:col-span-2"
            label="Company Address"
            icon={MapPin}
            value={record.address}
            long
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================
// READ-ONLY FIELD
// ============================================================

function Field({
  label,
  icon: Icon,
  value,
  placeholder = "—",
  long = false,
  className = "",
}) {
  const val = String(value ?? "").trim();

  return (
    <div className={className}>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}
      </label>

      <div
        className={`relative rounded-lg border border-slate-300 bg-white shadow-sm ${
          long ? "min-h-[48px]" : ""
        }`}
      >
        {Icon && (
          <Icon
            size={17}
            className="pointer-events-none absolute left-3 top-3 text-slate-400"
          />
        )}

        <div
          className={`min-h-[44px] break-words py-2.5 pr-3 text-sm text-slate-900 ${
            Icon ? "pl-10" : "pl-3"
          }`}
        >
          {val || (
            <span className="text-slate-400">
              {placeholder}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
